import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import SuccessStoryDialog from "@/components/SuccessStoryDialog";
import { userAPI, paymentsAPI } from "@/lib/api";
import { Target, FileText, TrendingUp, PenTool, ArrowRight, Clock, Sparkles, Award, CheckCircle, XCircle, AlertTriangle, Lightbulb, ClipboardList, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const SERVICE_MAP = {
  ats: { label: "Application Analyzer", icon: Target, color: "#003087", path: "/ats" },
  nhs_gen: { label: "NHS SI Generator", icon: FileText, color: "#00A3A6", path: "/nhs-generator" },
  coaching: { label: "Band Coaching", icon: TrendingUp, color: "#007F3B", path: "/band-coaching" },
  cv_rewrite: { label: "CV Rewriter", icon: PenTool, color: "#003087", path: "/cv-rewriter" },
  interview_sim: { label: "Interview Sim", icon: Award, color: "#7C3AED", path: "/interview" },
};

export default function Dashboard() {
  const { user, initData, setInitData } = useAuth();
  const [usage, setUsage] = useState(null);
  const [history, setHistory] = useState([]);
  const [searchParams] = useSearchParams();
  const [showSuccessStory, setShowSuccessStory] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);

  const formatActivityTimestamp = (value) => {
    if (!value) return "";
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return "";
    return dt.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  const loadData = async () => {
    try {
      const [usageRes, historyRes] = await Promise.all([userAPI.usage(), userAPI.history()]);
      setUsage(usageRes.data);
      const historyData = historyRes.data?.history || historyRes.data;
      setHistory(historyData || []);
    } catch {
      // silent fail on load
    }
  };

  const pollPayment = async (sessionId, attempts = 0) => {
    if (attempts >= 5) return;
    try {
      const res = await paymentsAPI.status(sessionId);
      if (res.data.payment_status === "paid") {
        toast.success(`Upgraded to ${res.data.plan} plan!`);
        window.history.replaceState({}, "", "/dashboard");
        loadData();
        return;
      }
      if (res.data.status === "expired") {
        toast.error("Payment session expired");
        return;
      }
      setTimeout(() => pollPayment(sessionId, attempts + 1), 2000);
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    // Use cached initData from auth if available (eliminates extra API calls on first load)
    if (initData) {
      setUsage(initData.usage);
      setHistory(initData.history || []);
      setInitData(null); // Clear cache so future navigations fetch fresh
    } else {
      loadData();
    }
    const sessionId = searchParams.get("session_id");
    if (sessionId) {
      pollPayment(sessionId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);


  const getUsagePercent = (key) => {
    if (!usage) return 0;
    const limit = usage.plan_config?.[`${key}_limit`];
    const used = usage.usage?.[key] || 0;
    if (limit === -1) return used > 0 ? Math.min((used / 10) * 100, 100) : 0;
    if (limit === 0) return 0;
    return Math.min((used / limit) * 100, 100);
  };

  const getUsageText = (key) => {
    if (!usage) return "...";
    const limit = usage.plan_config?.[`${key}_limit`];
    const raw = usage.usage?.[key] || 0;
    if (limit === -1) return `${raw} used (unlimited)`;
    if (limit === 0) return "Upgrade required";
    const used = Math.min(raw, limit);
    return `${used} / ${limit} used`;
  };

  const getResetDateLabel = () => {
    if (!usage?.period_end) return "";
    const dt = new Date(usage.period_end);
    if (Number.isNaN(dt.getTime())) return "";
    return dt.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const resetLabel = getResetDateLabel();


  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="dashboard-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#003087]" style={{ fontFamily: "Public Sans" }}>
              Welcome back, {user?.name?.split(" ")[0] || "there"}
            </h1>
            <p className="text-slate-500 mt-1">Here's your career toolkit at a glance.</p>
          </div>
          <Badge className={`text-sm px-3 py-1 ${user?.subscription_plan === "premium" ? "bg-[#00A3A6]/10 text-[#00A3A6]" : user?.subscription_plan === "pro" ? "bg-[#003087]/10 text-[#003087]" : "bg-slate-100 text-slate-600"}`}>
            {(user?.subscription_plan || "free").charAt(0).toUpperCase() + (user?.subscription_plan || "free").slice(1)} Plan
          </Badge>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4" data-testid="quick-actions">
          {Object.entries(SERVICE_MAP).map(([key, svc]) => {
            const limit = usage?.plan_config?.[`${key}_limit`];
            const locked = limit === 0;
            return (
              <Link key={key} to={locked ? "/pricing" : svc.path}>
                <Card className={`bg-white border border-slate-200 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 rounded-xl cursor-pointer ${locked ? "opacity-60" : ""}`} data-testid={`quick-action-${key}`}>
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between mb-3">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${svc.color}12` }}>
                        <svc.icon className="w-5 h-5" style={{ color: svc.color }} />
                      </div>
                      {locked ? (
                        <Badge variant="outline" className="text-xs border-slate-300 text-slate-400">Locked</Badge>
                      ) : (
                        <ArrowRight className="w-4 h-4 text-slate-400" />
                      )}
                    </div>
                    <h3 className="text-sm font-semibold text-slate-900 mb-1">{svc.label}</h3>
                    <p className="text-xs text-slate-500">{getUsageText(key)}</p>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>

        {/* Usage Overview */}
        <Card className="bg-white border border-slate-200 shadow-sm rounded-xl" data-testid="usage-overview">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-slate-900" style={{ fontFamily: "Public Sans" }}>Monthly Usage</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {Object.entries(SERVICE_MAP)
              .filter(([key]) => ["nhs_gen", "coaching", "cv_rewrite", "interview_sim"].includes(key))
              .map(([key, svc]) => (
              <div key={key} className="space-y-1.5">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-700 font-medium">{svc.label}</span>
                  <span className="text-slate-500">{getUsageText(key)}</span>
                </div>
                <Progress value={getUsagePercent(key)} className="h-2" />
                <p className="text-xs text-slate-500">Resets on: {resetLabel}</p>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card className="bg-white border border-slate-200 shadow-sm rounded-xl" data-testid="recent-activity">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-slate-900" style={{ fontFamily: "Public Sans" }}>Recent Activity</CardTitle>
          </CardHeader>
          <CardContent>
            {history.length === 0 ? (
              <div className="text-center py-10">
                <Sparkles className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 mb-4">No activity yet. Start by using one of the tools above.</p>
                <Link to="/nhs-generator">
                  <Button className="bg-[#003087] text-white hover:bg-[#002366]" data-testid="start-nhs-gen-btn">
                    Generate Supporting Info <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {history.slice(0, 5).map((item, i) => {
                  const svc = SERVICE_MAP[item.service_type] || SERVICE_MAP.ats;
                  return (
                    <div key={i} className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors" data-testid={`activity-${i}`}>
                      <div className="w-8 h-8 rounded-md flex items-center justify-center" style={{ backgroundColor: `${svc.color}12` }}>
                        <svc.icon className="w-4 h-4" style={{ color: svc.color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900">{svc.label}</p>
                        <p className="text-xs text-slate-500 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatActivityTimestamp(item.created_at)}
                        </p>
                      </div>
                      {item.result && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setSelectedActivity(item)}
                          data-testid={`activity-view-${item.output_id}`}
                        >
                          View
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
        {/* Success Story Prompt - shown after user has generated outputs */}
        {history.length >= 1 && (
          <Card className="bg-gradient-to-r from-[#003087]/5 to-[#00A3A6]/5 border border-[#003087]/20 shadow-sm rounded-xl" data-testid="success-story-prompt">
            <CardContent className="p-5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Award className="w-8 h-8 text-[#FFB81C]" />

                <div>
                  <p className="text-sm font-semibold text-[#003087]">Got shortlisted or offered a role?</p>
                  <p className="text-xs text-slate-500">Share your success story and inspire other NHS professionals.</p>
                </div>
              </div>
              <Button size="sm" className="bg-[#003087] text-white hover:bg-[#002366]" onClick={() => setShowSuccessStory(true)} data-testid="share-success-btn">
                Share Story
              </Button>
            </CardContent>
          </Card>
        )}

        {selectedActivity && (
          <Dialog open={!!selectedActivity} onOpenChange={() => setSelectedActivity(null)}>
            <DialogContent className="max-w-3xl">
              <DialogHeader>
                <DialogTitle>
                  {selectedActivity.service_type === "ats" && "Application Analysis"}
                  {selectedActivity.service_type === "nhs_gen" && "Supporting Information"}
                  {selectedActivity.service_type === "cv_rewrite" && "CV Rewrite"}
                  {selectedActivity.service_type === "coaching" && "Band Coaching"}
                  {selectedActivity.service_type === "interview_sim" && "Interview Result"}
                </DialogTitle>
                <p className="text-xs text-slate-500 mt-1">
                  Generated: {formatActivityTimestamp(selectedActivity.created_at)}
                </p>
              </DialogHeader>
              <div className="max-h-[60vh] overflow-y-auto text-sm text-slate-700 space-y-4">
                {/* Application Analyzer */}
                {selectedActivity.service_type === "ats" && selectedActivity.result && (() => {
                  const r = selectedActivity.result;
                  const scoreColor = (s) => s >= 70 ? "#007F3B" : s >= 40 ? "#FFB81C" : "#D5281B";
                  const ps = selectedActivity.ps_coverage || r.ps_coverage;
                  return (
                    <div className="space-y-4">
                      {/* Match Score */}
                      <div className="p-4 bg-slate-50 rounded-lg">
                        <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">Application Match Score</p>
                        <p className="text-3xl font-bold mt-1" style={{ color: scoreColor(r.score) }}>{r.score}%</p>
                        {r.summary && <p className="text-sm text-slate-600 mt-2">{r.summary}</p>}
                      </div>

                      {/* PS Coverage */}
                      {ps && (
                        <>
                          <div>
                            <h4 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                              <ClipboardList className="w-4 h-4 text-[#003087]" /> Person Specification Coverage
                            </h4>
                            <div className="grid grid-cols-2 gap-3">
                              <div className="p-3 bg-slate-50 rounded-lg text-center">
                                <p className="text-xs text-slate-500">Essential</p>
                                <p className="text-xl font-bold" style={{ color: scoreColor(ps.essential_coverage_pct) }}>{ps.essential_coverage_pct}%</p>
                              </div>
                              <div className="p-3 bg-slate-50 rounded-lg text-center">
                                <p className="text-xs text-slate-500">Desirable</p>
                                <p className="text-xl font-bold" style={{ color: scoreColor(ps.desirable_coverage_pct) }}>{ps.desirable_coverage_pct}%</p>
                              </div>
                            </div>
                          </div>

                          {/* Shortlisting Simulation */}
                          <div>
                            <h4 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                              <BarChart3 className="w-4 h-4 text-[#00A3A6]" /> Shortlisting Simulation
                            </h4>
                            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                              <div>
                                <p className="text-xs text-slate-500">Panel Score</p>
                                <p className="text-lg font-bold text-slate-900">{ps.panel_score} / {ps.panel_max}</p>
                              </div>
                              <div className="text-right">
                                <p className="text-xs text-slate-500">Likely Outcome</p>
                                <p className={`text-sm font-semibold ${ps.essential_coverage_pct >= 80 ? "text-[#007F3B]" : ps.essential_coverage_pct >= 50 ? "text-[#9A6700]" : "text-[#D5281B]"}`}>
                                  {ps.likely_outcome}
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* Missing & Weak Evidence */}
                          {(ps.missing_evidence?.length > 0 || ps.weak_evidence?.length > 0) && (
                            <div>
                              <h4 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                                <AlertTriangle className="w-4 h-4 text-[#D5281B]" /> Missing & Weak Evidence
                              </h4>
                              <div className="space-y-1.5">
                                {ps.missing_evidence?.map((m, i) => (
                                  <div key={`m-${i}`} className="flex items-start gap-2 text-sm">
                                    <XCircle className="w-3.5 h-3.5 text-[#D5281B] mt-0.5 flex-shrink-0" />
                                    <span>{m}</span>
                                  </div>
                                ))}
                                {ps.weak_evidence?.map((w, i) => (
                                  <div key={`w-${i}`} className="flex items-start gap-2 text-sm">
                                    <AlertTriangle className="w-3.5 h-3.5 text-[#FFB81C] mt-0.5 flex-shrink-0" />
                                    <span>{w}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </>
                      )}

                      {/* Strengths */}
                      {r.strengths?.length > 0 && (
                        <div>
                          <h4 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-[#007F3B]" /> Strengths
                          </h4>
                          <ul className="list-disc list-inside space-y-1">
                            {r.strengths.map((s, i) => <li key={i}>{s}</li>)}
                          </ul>
                        </div>
                      )}

                      {/* Missing Skills */}
                      {r.missing_skills?.length > 0 && (
                        <div>
                          <h4 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                            <XCircle className="w-4 h-4 text-[#D5281B]" /> Missing Skills
                          </h4>
                          <ul className="list-disc list-inside space-y-1">
                            {r.missing_skills.map((s, i) => <li key={i}>{s}</li>)}
                          </ul>
                        </div>
                      )}

                      {/* Areas of Improvement */}
                      {r.areas_of_improvement?.length > 0 && (
                        <div>
                          <h4 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                            <Lightbulb className="w-4 h-4 text-[#FFB81C]" /> Areas of Improvement
                          </h4>
                          <div className="space-y-3">
                            {r.areas_of_improvement.map((item, i) => (
                              <div key={i} className="p-3 bg-slate-50 rounded-lg space-y-2">
                                <p className="font-medium text-slate-900">{item.requirement}</p>
                                <p className="text-slate-600">{item.current_gap}</p>
                                <Separator />
                                <p className="text-xs text-slate-500 uppercase font-medium">Evidence to Add</p>
                                <p className="text-slate-700">{item.evidence_to_add}</p>
                                {item.rewrite_suggestion && (
                                  <>
                                    <p className="text-xs text-slate-500 uppercase font-medium">Suggestion</p>
                                    <p className="text-slate-700 italic">{item.rewrite_suggestion}</p>
                                  </>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Recommendations */}
                      {r.recommendations?.length > 0 && (
                        <div>
                          <h4 className="font-semibold text-slate-900 mb-2">Recommendations</h4>
                          <ul className="list-disc list-inside space-y-1">
                            {r.recommendations.map((rec, i) => <li key={i}>{rec}</li>)}
                          </ul>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* NHS SI */}
                {selectedActivity.service_type === "nhs_gen" && selectedActivity.result?.supporting_statement && (
                  <div className="whitespace-pre-wrap">{selectedActivity.result.supporting_statement}</div>
                )}

                {/* CV Rewrite */}
                {selectedActivity.service_type === "cv_rewrite" && selectedActivity.result && (
                  <div className="space-y-4">
                    {selectedActivity.result.rewritten_cv && (
                      <div className="whitespace-pre-wrap">{selectedActivity.result.rewritten_cv}</div>
                    )}
                    {selectedActivity.result.changes_made?.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-slate-900 mb-2">Changes Made</h4>
                        <ul className="list-disc list-inside space-y-1">
                          {selectedActivity.result.changes_made.map((c, i) => <li key={i}>{c}</li>)}
                        </ul>
                      </div>
                    )}
                    {selectedActivity.result.improvement_score != null && (
                      <p className="text-sm font-medium">Improvement Score: <span className="text-[#007F3B]">{selectedActivity.result.improvement_score}/100</span></p>
                    )}
                    {selectedActivity.result.tips?.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-slate-900 mb-2">Tips</h4>
                        <ul className="list-disc list-inside space-y-1">
                          {selectedActivity.result.tips.map((t, i) => <li key={i}>{t}</li>)}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {/* Band Coaching */}
                {selectedActivity.service_type === "coaching" && selectedActivity.result && (
                  <div className="space-y-4">
                    {selectedActivity.result.gap_analysis && (
                      <div>
                        <h4 className="font-semibold text-slate-900 mb-1">Gap Analysis</h4>
                        <p className="whitespace-pre-wrap">{selectedActivity.result.gap_analysis}</p>
                      </div>
                    )}
                    {selectedActivity.result.missing_competencies?.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-slate-900 mb-2">Missing Competencies</h4>
                        <div className="space-y-2">
                          {selectedActivity.result.missing_competencies.map((mc, i) => (
                            <div key={i} className="p-3 bg-slate-50 rounded-lg">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-slate-900">{mc.competency}</span>
                                <Badge variant="outline" className={mc.priority === "high" ? "border-red-200 text-red-700" : mc.priority === "medium" ? "border-yellow-200 text-yellow-700" : "border-slate-200 text-slate-500"}>{mc.priority}</Badge>
                              </div>
                              <p className="text-sm text-slate-600 mt-1">{mc.description}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {selectedActivity.result.leadership_assessment && (
                      <div>
                        <h4 className="font-semibold text-slate-900 mb-1">Leadership Assessment</h4>
                        <p className="whitespace-pre-wrap">{selectedActivity.result.leadership_assessment}</p>
                      </div>
                    )}
                    {selectedActivity.result.roadmap?.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-slate-900 mb-2">Progression Roadmap</h4>
                        <div className="space-y-2">
                          {selectedActivity.result.roadmap.map((step, i) => (
                            <div key={i} className="p-3 bg-slate-50 rounded-lg">
                              <p className="font-medium text-slate-900 text-sm">{step.month}</p>
                              <ul className="list-disc list-inside text-sm text-slate-600 mt-1">
                                {(step.actions || []).map((a, ai) => <li key={ai}>{a}</li>)}
                              </ul>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {selectedActivity.result.key_recommendations?.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-slate-900 mb-2">Key Recommendations</h4>
                        <ul className="list-disc list-inside space-y-1">
                          {selectedActivity.result.key_recommendations.map((r, i) => <li key={i}>{r}</li>)}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {/* Interview Sim */}
                {selectedActivity.service_type === "interview_sim" && selectedActivity.result && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-4">
                      <div className="text-center">
                        <p className="text-3xl font-bold" style={{ color: selectedActivity.result.overall_score >= 70 ? "#007F3B" : selectedActivity.result.overall_score >= 40 ? "#FFB81C" : "#D5281B" }}>
                          {selectedActivity.result.overall_score}/100
                        </p>
                        <Badge className={
                          selectedActivity.result.overall_assessment === "Strong" ? "bg-[#007F3B]/10 text-[#007F3B]" :
                          selectedActivity.result.overall_assessment === "Moderate" ? "bg-[#FFB81C]/10 text-[#9A6700]" :
                          selectedActivity.result.overall_assessment === "No Response" ? "bg-slate-200 text-slate-600" :
                          "bg-[#D5281B]/10 text-[#D5281B]"
                        }>{selectedActivity.result.overall_assessment}</Badge>
                      </div>
                      {selectedActivity.result.summary && (
                        <p className="text-sm text-slate-600 flex-1">{selectedActivity.result.summary}</p>
                      )}
                    </div>
                    {selectedActivity.result.band_readiness && (
                      <p className="text-xs text-slate-500 bg-slate-50 p-2 rounded">{selectedActivity.result.band_readiness}</p>
                    )}
                    {selectedActivity.result.key_strengths?.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-slate-900 mb-2">Key Strengths</h4>
                        <ul className="list-disc list-inside space-y-1">
                          {selectedActivity.result.key_strengths.map((s, i) => <li key={i}>{s}</li>)}
                        </ul>
                      </div>
                    )}
                    {selectedActivity.result.development_areas?.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-slate-900 mb-2">Areas to Develop</h4>
                        <ul className="list-disc list-inside space-y-1">
                          {selectedActivity.result.development_areas.map((a, i) => <li key={i}>{a}</li>)}
                        </ul>
                      </div>
                    )}
                    {selectedActivity.result.answer_feedback?.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-slate-900 mb-2">Answer Feedback</h4>
                        <div className="space-y-2">
                          {selectedActivity.result.answer_feedback.map((fb, i) => (
                            <div key={i} className="p-3 bg-slate-50 rounded-lg">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-medium text-sm text-slate-900">Q{fb.question_id}</span>
                                <Badge variant="outline" className={
                                  fb.assessment === "Strong" ? "bg-[#007F3B]/10 text-[#007F3B]" :
                                  fb.assessment === "Moderate" ? "bg-[#FFB81C]/10 text-[#9A6700]" :
                                  fb.assessment === "No Response" ? "bg-slate-200 text-slate-600" :
                                  "bg-[#D5281B]/10 text-[#D5281B]"
                                }>{fb.assessment}</Badge>
                                <span className="text-xs text-slate-500 ml-auto">{fb.score}/10</span>
                              </div>
                              {fb.panel_competency_being_assessed && (
                                <p className="text-xs text-slate-500">{fb.panel_competency_being_assessed}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {selectedActivity.result.preparation_recommendations?.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-slate-900 mb-2">Preparation Recommendations</h4>
                        <ul className="list-disc list-inside space-y-1">
                          {selectedActivity.result.preparation_recommendations.map((r, i) => <li key={i}>{r}</li>)}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
        )}

        <SuccessStoryDialog open={showSuccessStory} onOpenChange={setShowSuccessStory} />
      </div>
    </DashboardLayout>
  );
}
